import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings,
  Mail,
  Phone,
  MessageSquare,
  Search,
  Save,
  User,
  Bell,
  BellOff,
  Check,
  X,
  Edit,
  Users
} from 'lucide-react';
import { communicationService, patientService } from '../../services/api';
import { Patient, NotificationPreference } from '../../types';

export default function PatientPreferences() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<NotificationPreference | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPrefs, setEditingPrefs] = useState<Partial<NotificationPreference>>({});
  
  const queryClient = useQueryClient();

  // Récupérer les préférences des patients
  const { data: preferencesData, isLoading } = useQuery({
    queryKey: ['patient-preferences', searchTerm],
    queryFn: () => {
      const queryParams = searchTerm ? { search: searchTerm } : {};
      return communicationService.getPreferences(queryParams).then(res => res.data);
    }
  });

  // Récupérer tous les patients pour créer des préférences
  const { data: patientsData } = useQuery({
    queryKey: ['patients'],
    queryFn: () => patientService.getAll().then(res => res.data)
  });

  const preferences: NotificationPreference[] = preferencesData?.results || [];
  const allPatients: Patient[] = patientsData?.results || [];

  // Patients sans préférences configurées
  const patientsWithoutPrefs = allPatients.filter(patient => 
    !preferences.some(pref => pref.patient === patient.id)
  );

  // Mutation pour mettre à jour les préférences
  const updatePreferencesMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<NotificationPreference> }) =>
      communicationService.updatePreferences(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-preferences'] });
      setShowEditModal(false);
      setSelectedPatient(null);
    },
    onError: (error) => {
      console.error('Erreur mise à jour préférences:', error);
      alert('Erreur lors de la mise à jour des préférences');
    }
  });

  // Mutation pour créer des préférences
  const createPreferencesMutation = useMutation({
    mutationFn: (data: Partial<NotificationPreference>) =>
      communicationService.createPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-preferences'] });
    },
    onError: (error) => {
      console.error('Erreur création préférences:', error);
      alert('Erreur lors de la création des préférences');
    }
  });

  const handleEditPreferences = (preference: NotificationPreference) => {
    setSelectedPatient(preference);
    setEditingPrefs(preference);
    setShowEditModal(true);
  };

  const handleSavePreferences = () => {
    if (selectedPatient && editingPrefs) {
      updatePreferencesMutation.mutate({
        id: selectedPatient.id,
        data: editingPrefs
      });
    }
  };

  const handleCreateDefaultPreferences = async (patient: Patient) => {
    const defaultPrefs = {
      patient: patient.id,
      appointment_reminders_email: true,
      appointment_reminders_sms: false,
      appointment_reminders_whatsapp: false,
      payment_reminders_email: true,
      payment_reminders_sms: false,
      marketing_emails: false,
      birthday_wishes: true
    };
    
    createPreferencesMutation.mutate(defaultPrefs);
  };

  const handleBulkUpdate = (field: keyof NotificationPreference, value: boolean) => {
    if (window.confirm(`Êtes-vous sûr de vouloir ${value ? 'activer' : 'désactiver'} "${field}" pour tous les patients ?`)) {
      preferences.forEach(pref => {
        updatePreferencesMutation.mutate({
          id: pref.id,
          data: { [field]: value }
        });
      });
    }
  };

  const PreferenceToggle = ({ 
    enabled, 
    onChange, 
    label, 
    icon: Icon 
  }: { 
    enabled: boolean; 
    onChange: (value: boolean) => void; 
    label: string; 
    icon: React.ComponentType<any>;
  }) => (
    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
      <div className="flex items-center">
        <Icon className="h-4 w-4 mr-2 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
          enabled ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
            enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Préférences de communication</h2>
        <p className="text-gray-600">Gérez les préférences de notification de chaque patient</p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Patients configurés</p>
              <p className="text-lg font-semibold text-gray-900">{preferences.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Mail className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Emails activés</p>
              <p className="text-lg font-semibold text-gray-900">
                {preferences.filter(p => p.appointment_reminders_email).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Phone className="h-5 w-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">SMS activés</p>
              <p className="text-lg font-semibold text-gray-900">
                {preferences.filter(p => p.appointment_reminders_sms).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <BellOff className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Sans préférences</p>
              <p className="text-lg font-semibold text-gray-900">{patientsWithoutPrefs.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un patient..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Actions en masse */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Actions en masse</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleBulkUpdate('appointment_reminders_email', true)}
            className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
          >
            Activer emails RDV
          </button>
          <button
            onClick={() => handleBulkUpdate('appointment_reminders_sms', true)}
            className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm"
          >
            Activer SMS RDV
          </button>
          <button
            onClick={() => handleBulkUpdate('marketing_emails', false)}
            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
          >
            Désactiver promotions
          </button>
        </div>
      </div>

      {/* Patients sans préférences */}
      {patientsWithoutPrefs.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-800 mb-4">
            Patients sans préférences configurées ({patientsWithoutPrefs.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patientsWithoutPrefs.slice(0, 6).map((patient) => (
              <div key={patient.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{patient.nom} {patient.prenoms}</p>
                  <p className="text-sm text-gray-500">{patient.email} • {patient.telephone}</p>
                </div>
                <button
                  onClick={() => handleCreateDefaultPreferences(patient)}
                  className="px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700"
                >
                  Configurer
                </button>
              </div>
            ))}
          </div>
          {patientsWithoutPrefs.length > 6 && (
            <p className="text-sm text-yellow-700 mt-2">
              Et {patientsWithoutPrefs.length - 6} autres patients...
            </p>
          )}
        </div>
      )}

      {/* Liste des préférences */}
      <div className="bg-white rounded-lg shadow-sm border">
        {preferences.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Settings className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">Aucune préférence trouvée</p>
            <p>Les patients n'ont pas encore configuré leurs préférences</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {preferences.map((preference) => (
              <div key={preference.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <User className="h-5 w-5 text-gray-400 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900">
                        {preference.patient_details.nom} {preference.patient_details.prenoms}
                      </h3>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-4">
                      {preference.patient_details.email} • {preference.patient_details.telephone}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Rappels de RDV */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Rappels de RDV</h4>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-blue-500" />
                            <span className="text-sm text-gray-600">Email</span>
                            {preference.appointment_reminders_email ? (
                              <Check className="h-4 w-4 ml-2 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 ml-2 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-2 text-purple-500" />
                            <span className="text-sm text-gray-600">SMS</span>
                            {preference.appointment_reminders_sms ? (
                              <Check className="h-4 w-4 ml-2 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 ml-2 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center">
                            <MessageSquare className="h-4 w-4 mr-2 text-green-500" />
                            <span className="text-sm text-gray-600">WhatsApp</span>
                            {preference.appointment_reminders_whatsapp ? (
                              <Check className="h-4 w-4 ml-2 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 ml-2 text-red-500" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Rappels de paiement */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Rappels de paiement</h4>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-blue-500" />
                            <span className="text-sm text-gray-600">Email</span>
                            {preference.payment_reminders_email ? (
                              <Check className="h-4 w-4 ml-2 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 ml-2 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-2 text-purple-500" />
                            <span className="text-sm text-gray-600">SMS</span>
                            {preference.payment_reminders_sms ? (
                              <Check className="h-4 w-4 ml-2 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 ml-2 text-red-500" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Autres notifications */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2">Autres</h4>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <Bell className="h-4 w-4 mr-2 text-yellow-500" />
                            <span className="text-sm text-gray-600">Anniversaire</span>
                            {preference.birthday_wishes ? (
                              <Check className="h-4 w-4 ml-2 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 ml-2 text-red-500" />
                            )}
                          </div>
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-blue-500" />
                            <span className="text-sm text-gray-600">Promotions</span>
                            {preference.marketing_emails ? (
                              <Check className="h-4 w-4 ml-2 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 ml-2 text-red-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleEditPreferences(preference)}
                    className="flex items-center px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Modifier
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal d'édition des préférences */}
      {showEditModal && selectedPatient && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEditModal(false)}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Préférences de {selectedPatient.patient_details.nom} {selectedPatient.patient_details.prenoms}
                  </h3>
                  <button 
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Rappels de RDV */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Rappels de rendez-vous</h4>
                    <div className="space-y-3">
                      <PreferenceToggle
                        enabled={editingPrefs.appointment_reminders_email || false}
                        onChange={(value) => setEditingPrefs(prev => ({ ...prev, appointment_reminders_email: value }))}
                        label="Recevoir par email"
                        icon={Mail}
                      />
                      <PreferenceToggle
                        enabled={editingPrefs.appointment_reminders_sms || false}
                        onChange={(value) => setEditingPrefs(prev => ({ ...prev, appointment_reminders_sms: value }))}
                        label="Recevoir par SMS"
                        icon={Phone}
                      />
                      <PreferenceToggle
                        enabled={editingPrefs.appointment_reminders_whatsapp || false}
                        onChange={(value) => setEditingPrefs(prev => ({ ...prev, appointment_reminders_whatsapp: value }))}
                        label="Recevoir par WhatsApp"
                        icon={MessageSquare}
                      />
                    </div>
                  </div>

                  {/* Rappels de paiement */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Rappels de paiement</h4>
                    <div className="space-y-3">
                      <PreferenceToggle
                        enabled={editingPrefs.payment_reminders_email || false}
                        onChange={(value) => setEditingPrefs(prev => ({ ...prev, payment_reminders_email: value }))}
                        label="Recevoir par email"
                        icon={Mail}
                      />
                      <PreferenceToggle
                        enabled={editingPrefs.payment_reminders_sms || false}
                        onChange={(value) => setEditingPrefs(prev => ({ ...prev, payment_reminders_sms: value }))}
                        label="Recevoir par SMS"
                        icon={Phone}
                      />
                    </div>
                  </div>

                  {/* Autres notifications */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Autres notifications</h4>
                    <div className="space-y-3">
                      <PreferenceToggle
                        enabled={editingPrefs.birthday_wishes || false}
                        onChange={(value) => setEditingPrefs(prev => ({ ...prev, birthday_wishes: value }))}
                        label="Vœux d'anniversaire"
                        icon={Bell}
                      />
                      <PreferenceToggle
                        enabled={editingPrefs.marketing_emails || false}
                        onChange={(value) => setEditingPrefs(prev => ({ ...prev, marketing_emails: value }))}
                        label="Emails promotionnels"
                        icon={Mail}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  onClick={handleSavePreferences}
                  disabled={updatePreferencesMutation.isLoading}
                  className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50 sm:ml-3 sm:w-auto"
                >
                  {updatePreferencesMutation.isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Sauvegarder
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}